import React, { useState, useEffect } from 'react';
import axiosInstance from './axiosInstance';
import './AdminDashboard.css'; // Reuse the same CSS file

const AdminWordOfTheDay = ({ showNames }) => {
    const [adminFirstName, setAdminFirstName] = useState('Admin');
    const [isMobile, setIsMobile] = useState(false);
    const [wordOfTheDay, setWordOfTheDay] = useState(null);
    const [loadingWord, setLoadingWord] = useState(true);
    const [errorWord, setErrorWord] = useState('');
    const [likeMessages] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [wordToDelete, setWordToDelete] = useState(null);
    const [wordToUpdate, setWordToUpdate] = useState(null);
    const [updateFormData, setUpdateFormData] = useState({ title: '', body: '' });
    const [createFormData, setCreateFormData] = useState({ title: '', body: '' });

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 768) {
                setIsMobile(true); // For mobile screens
            } else {
                setIsMobile(false); // For larger screens
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Run once to check initial window size

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const fetchAdminFirstName = async () => {
            try {
                const response = await axiosInstance.get('/admin/first-name');
                setAdminFirstName(response.data.first_name);
            } catch (err) {
                console.error('Failed to fetch admin first name', err);
            }
        };

        fetchAdminFirstName();
    }, []);

    const fetchWordOfTheDay = async () => {
        try {
            const response = await axiosInstance.get('/word-of-day/current');
            setWordOfTheDay(response.data);
        } catch (err) {
            console.error(err);
            setErrorWord('Failed to fetch the current Word of the Day');
        } finally {
            setLoadingWord(false);
        }
    };

    useEffect(() => {
        fetchWordOfTheDay();
    }, []);

    const handleUpdateWordOfTheDay = async (id) => {
        try {
            await axiosInstance.put(`/word-of-day/${id}`, updateFormData);
            setShowUpdateModal(false);
            setWordToUpdate(null);
            fetchWordOfTheDay(); // Refetch data after update
        } catch (err) {
            console.error('Failed to update Word of the Day:', err);
        }
    };

    const handleCreateWordOfTheDay = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.post('/word-of-day', createFormData);
            setShowCreateModal(false);
            setCreateFormData({ title: '', body: '' });
            fetchWordOfTheDay(); // Refetch data after create
        } catch (err) {
            console.error('Failed to create Word of the Day:', err);
        }
    };

    const handleDeleteWordOfTheDay = async () => {
        try {
            await axiosInstance.delete(`/word-of-day/${wordToDelete}`);
            setShowDeleteModal(false);
            setWordToDelete(null);
            fetchWordOfTheDay(); // Refetch data after delete
        } catch (err) {
            console.error('Failed to delete Word of the Day:', err);
        }
    };

    const handleUpdateFormChange = (e) => {
        const { name, value } = e.target;
        setUpdateFormData({ ...updateFormData, [name]: value });
    };

    const handleCreateFormChange = (e) => {
        const { name, value } = e.target;
        setCreateFormData({ ...createFormData, [name]: value });
    };

    const cardStyle = {
        padding: '16px',
        borderRadius: '8px',
        backgroundColor: '#2d2d2d',
        color: 'white',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        transition: 'transform 0.3s, box-shadow 0.3s, border-color 0.3s',
        border: '2px solid #ff7c0a',
    };

    const titleStyle = {
        fontSize: '1.5rem',
        fontWeight: 'bold',
        marginTop: '16px',
        color: '#ff7c0a',
    };

    const subtitleStyle = {
        fontSize: '1.25rem',
        fontWeight: 'bold',
        marginTop: '8px',
        color: '#ff7c0a',
    };

    const bodyStyle = {
        fontSize: '1rem',
        marginTop: '8px',
        color: 'white',
    };

    const iconStyle = {
        marginRight: '8px',
        cursor: 'pointer',
    };

    const messageStyle = {
        color: '#ff7c0a',
        marginTop: '8px',
    };

    const buttonStyle = {
        backgroundColor: '#ff7c0a',
        color: 'white',
        padding: '10px 20px',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        margin: '5px',
        fontWeight: 'semibold',
    };

    const deleteButtonStyle = {
        ...buttonStyle,
        backgroundColor: 'white',
        color: '#ff7c0a',
        fontWeight: 'semibold',
    };

    const cancelButtonStyle = {
        ...buttonStyle,
        backgroundColor: 'white',
        color: '#ff7c0a',
        fontWeight: 'semibold',
    };

    return (
        <div className={`admin-dashboard-container ${showNames ? 'show-names' : ''}`}>
            <div className="mini-header">
                <h1 className="mini-header-title">Admin Word of the Day</h1>
                <div className="mini-header-right">
                    <span className="admin-name">{adminFirstName}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 32 32">
                        <path fill="currentColor" d="M23 9A7 7 0 1 1 9 9a7 7 0 0 1 14 0m-2 0a5 5 0 1 0-10 0a5 5 0 0 0 10 0M7.5 18A3.5 3.5 0 0 0 4 21.5v.5c0 2.393 1.523 4.417 3.685 5.793C9.859 29.177 12.802 30 16 30s6.14-.823 8.315-2.207C26.477 26.417 28 24.393 28 22v-.5a3.5 3.5 0 0 0-3.5-3.5zM6 21.5A1.5 1.5 0 0 1 7.5 20h17a1.5 1.5 0 0 1 1.5 1.5v.5c0 1.473-.94 2.949-2.759 4.106C21.434 27.256 18.877 28 16 28s-5.434-.744-7.241-1.894C6.939 24.95 6 23.472 6 22z" />
                    </svg>
                </div>
            </div>
            <div className="dashboard-container">
                <button className="send-newsletters-button" onClick={() => setShowCreateModal(true)}>Create Word of the Day</button>
                <div className="dashboard-content">
                    {loadingWord ? (
                        <p>Loading Word of the Day...</p>
                    ) : errorWord ? (
                        <p>{errorWord}</p>
                    ) : wordOfTheDay ? (
                        <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'center' }}>
                            <div style={{ ...cardStyle, width: isMobile ? '100%' : '60%' }}>
                                <h3 style={titleStyle}>{wordOfTheDay.title}</h3>
                                {wordOfTheDay.subtitle && <h4 style={subtitleStyle}>{wordOfTheDay.subtitle}</h4>}
                                <p style={bodyStyle}>{wordOfTheDay.body}</p>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={iconStyle}>👍</span>
                                        <span>{wordOfTheDay.likes}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={iconStyle}>💬</span>
                                        <span>{wordOfTheDay.comments}</span>
                                    </div>
                                </div>
                                {likeMessages.wordOfTheDay && <p style={messageStyle}>{likeMessages.wordOfTheDay}</p>}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
                                    <button style={buttonStyle} onClick={() => { setShowUpdateModal(true); setWordToUpdate(wordOfTheDay); setUpdateFormData({ title: wordOfTheDay.title, body: wordOfTheDay.body }); }}>Update</button>
                                    <button style={deleteButtonStyle} onClick={() => { setShowDeleteModal(true); setWordToDelete(wordOfTheDay.id); }}>Delete</button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <p>No Word of the Day found</p>
                    )}
                </div>
            </div>
            {showDeleteModal && (
                <div className="modal">
                    <div style={cardStyle}>
                        <h3 style={titleStyle}>Are you sure you want to delete this Word of the Day?</h3>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                            <button style={buttonStyle} onClick={handleDeleteWordOfTheDay}>Delete</button>
                            <button style={cancelButtonStyle} onClick={() => setShowDeleteModal(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
            {showUpdateModal && (
                <div className="modal">
                    <div style={cardStyle}>
                        <h3 style={titleStyle}>Update Word of the Day</h3>
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                        <form onSubmit={(e) => { e.preventDefault(); handleUpdateWordOfTheDay(wordToUpdate.id); }}>
                            <div className="form-group">
                                <label htmlFor="title" className="form-label" style={{color: '#ff7c0a'}}>Title</label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    className="form-input"
                                    value={updateFormData.title}
                                    onChange={handleUpdateFormChange}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="body" className="form-label" style={{color: '#ff7c0a'}}>Body</label>
                                <textarea
                                    id="body"
                                    name="body"
                                    className="form-input"
                                    value={updateFormData.body}
                                    onChange={handleUpdateFormChange}
                                />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                                <button type="submit" style={buttonStyle}>Update</button>
                                <button type="button" style={cancelButtonStyle} onClick={() => setShowUpdateModal(false)}>Cancel</button>
                            </div>
                        </form>
                        </div>
                    </div>
                </div>
            )}
            {showCreateModal && (
                <div className="modal">
                    <div style={cardStyle}>
                        <h3 style={titleStyle}>Create Word of the Day</h3>
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                        <form onSubmit={handleCreateWordOfTheDay}>
                            <div className="form-group">
                                <label htmlFor="title" className="form-label" style={{color: '#ff7c0a'}}>Title</label>
                                <input
                                    type="text"
                                    id="title"
                                    name="title"
                                    className="form-input"
                                    value={createFormData.title}
                                    onChange={handleCreateFormChange}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="body" className="form-label" style={{color: '#ff7c0a'}}>Body</label>
                                <textarea
                                    id="body"
                                    name="body"
                                    className="form-input"
                                    value={createFormData.body}
                                    onChange={handleCreateFormChange}
                                />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                                <button type="submit" style={buttonStyle}>Create</button>
                                <button type="button" style={cancelButtonStyle} onClick={() => setShowCreateModal(false)}>Cancel</button>
                            </div>
                        </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminWordOfTheDay;